import {
   collection,
   query,
   where,
   getDocs,
   addDoc,
   updateDoc,
   deleteDoc,
   doc,
   getDoc,
   serverTimestamp,
   Timestamp,
   orderBy,
   DocumentSnapshot,
   QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { User } from '../contexts/UserContext';
import axios from 'axios';
import chatService from './ChatService';

export type TripRole = 'owner' | 'editor' | 'viewer' | 'participant';

export interface TripParticipant {
   id: string;
   tripId: string;
   userId: string;
   userEmail: string;
   userName: string;
   role: TripRole;
   invitedBy: string;
   invitedAt: Date;
   status: 'pending' | 'accepted' | 'declined';
   acceptedAt?: Date;
   userPhotoURL?: string;
}

export interface TripInvitation {
   id: string;
   tripId: string;
   tripTitle: string;
   invitedBy: {
      id: string;
      name: string;
      email: string;
      photoURL?: string;
   };
   role: TripRole;
   invitedAt: Date;
}

class TripSharingService {
   /**
    * Get all participants for a specific trip
    */
   async getTripParticipants(tripId: string): Promise<TripParticipant[]> {
      try {
         const q = query(
            collection(db, 'tripParticipants'),
            where('tripId', '==', tripId),
            orderBy('invitedAt', 'desc')
         );

         const querySnapshot = await getDocs(q);
         const participants: TripParticipant[] = [];

         querySnapshot.forEach((doc) => {
            const data = doc.data();
            participants.push({
               id: doc.id,
               tripId: data.tripId,
               userId: data.userId,
               userEmail: data.userEmail,
               userName: data.userName,
               role: data.role,
               invitedBy: data.invitedBy,
               invitedAt: data.invitedAt.toDate(),
               status: data.status,
               acceptedAt: data.acceptedAt?.toDate(),
               userPhotoURL: data.userPhotoURL,
            });
         });

         return participants;
      } catch (error) {
         console.error('Error getting trip participants:', error);
         throw error;
      }
   }

   /**
    * Check if a user has access to a trip
    */
   async checkTripAccess(
      tripId: string,
      userId: string
   ): Promise<{ hasAccess: boolean; role?: TripRole }> {
      try {
         // Check if user is the trip owner first (in the trips collection)
         const tripDoc = await getDoc(doc(db, 'trips', tripId));
         
         if (!tripDoc.exists()) {
            return { hasAccess: false };
         }
         
         const tripData = tripDoc.data();
         
         // If user is the trip owner, they have owner access
         if (tripData.createdBy === userId) {
            return { hasAccess: true, role: 'owner' };
         }
         
         // Check if user is a participant
         const participantQuery = query(
            collection(db, 'tripParticipants'),
            where('tripId', '==', tripId),
            where('userId', '==', userId),
            where('status', '==', 'accepted')
         );
         
         const participantSnapshot = await getDocs(participantQuery);
         
         if (participantSnapshot.empty) {
            return { hasAccess: false };
         }
         
         const participantData = participantSnapshot.docs[0].data();
         const role = participantData.role as TripRole;
         
         return { hasAccess: true, role };
      } catch (error) {
         console.error('Error checking trip access:', error);
         return { hasAccess: false };
      }
   }

   sendInviteEmail = async (tripId: string, email: string, sender: string) => {
      try {
         await axios.post(
            `${process.env.REACT_APP_API_URL}/api/trip/invite`,
            {
               receiver: email,
               invite_sender: sender,
               tripId,
            },
            {
               headers: {
                  'Content-Type': 'application/json',
               },
            }
         );
      } catch (error) {
         throw error;
      }
   };

   async inviteUserToTrip(
      tripId: string,
      userEmail: string,
      role: string,
      sender: string
   ): Promise<any> {
      try {
         const userQuery = query(collection(db, 'users'), where('email', '==', userEmail));
         const userSnapshot = await getDocs(userQuery);

         const trip = await getDoc(doc(db, 'trips', tripId));

         if (!trip.exists()) {
            alert('Trip not found');
            return;
         }

         const existingTrip = trip.data();

         const userCollab =
            existingTrip?.collaborators?.find((c: any) => {
               return c?.email === userEmail;
            }) || null;

         if (userCollab) {
            await this.sendInviteEmail(tripId, userEmail, sender);
            alert('Already sent an invite');
            return;
         }

         let userId = '';
         let userName = '';
         let userPhotoURL = '';

         if (!userSnapshot.empty) {
            const userData = userSnapshot.docs[0].data();
            userId = userSnapshot.docs[0].id;
            userName = userData.displayName || userEmail.split('@')[0];
            userPhotoURL = userData.photoURL || '';
         } else {
            userName = userEmail.split('@')[0];
         }

         const updatedCollaborators = [
            ...(existingTrip?.collaborators || []),
            {
               email: userEmail,
               status: role,
               isAccepted: false,
               id: userId || '',
               userName,
               userPhotoURL,
            },
         ];

         await updateDoc(doc(db, 'trips', tripId), {
            collaborators: updatedCollaborators,
         });
         await this.sendInviteEmail(tripId, userEmail, sender);
      } catch (error) {
         console.error('Error inviting user to trip:', error);
         throw error;
      }
   }

   async acceptInvitation(tripId: string, userEmail: string): Promise<void> {
      try {
         const userQuery = query(collection(db, 'users'), where('email', '==', userEmail));
         const userSnapshot = await getDocs(userQuery);

         if (userSnapshot.empty) {
            window.location.assign('/register');
            return;
         }

         const trip = await getDoc(doc(db, 'trips', tripId));

         if (!trip.exists()) {
            alert('Trip not found');
            return;
         }

         const existingTrip = trip.data();

         const userCollab =
            existingTrip?.collaborators?.find((c: any) => {
               return c?.email === userEmail;
            }) || null;

         if (!userCollab) {
            alert('User not found');
            return;
         }

         if (userCollab?.isAccepted) {
            alert('Already accepted the trip');
            return;
         }

         const collaborators = existingTrip?.collaborators?.map((c: any) => {
            if (c?.email === userEmail) {
               return {
                  ...c,
                  isAccepted: true,
               };
            } else {
               return c;
            }
         });

         await updateDoc(doc(db, 'trips', tripId), {
            collaborators: [...collaborators],
         });
         
         // Add user to trip chat channel
         if (userCollab.id) {
            try {
               await chatService.addUserToTripChat(tripId, userCollab.id);

            } catch (chatErr) {
               console.error('Error adding user to trip chat:', chatErr);
               // Continue anyway - chat is not critical for trip functionality
            }
         }
         
         alert(`Thanks for accepting the trip timeline`);
      } catch (error) {
         console.error('Error accepting invitation:', error);
         throw error;
      }
   }

   /**
    * Decline a trip invitation
    */
   async declineInvitation(invitationId: string, user: User): Promise<void> {
      try {
         const invitationRef = doc(db, 'tripParticipants', invitationId);
         const invitationDoc = await getDoc(invitationRef);

         if (!invitationDoc.exists()) {
            throw new Error('Invitation not found');
         }

         const invitationData = invitationDoc.data();

         // Verify the invitation is for this user
         if (invitationData.userEmail !== user.email && invitationData.userId !== user.uid) {
            throw new Error('This invitation is not for you');
         }

         // Update the invitation
         await updateDoc(invitationRef, {
            status: 'declined',
            userId: user.uid,
         });
      } catch (error) {
         console.error('Error declining invitation:', error);
         throw error;
      }
   }

   /**
    * Remove a user from a trip
    */
   async removeUserFromTrip(tripId: string, userId: string, currentUserId: string): Promise<void> {
      try {
         // First check if the current user has permission to remove users
         const tripDoc = await getDoc(doc(db, 'trips', tripId));
         
         if (!tripDoc.exists()) {
            throw new Error('Trip not found');
         }
         
         const tripData = tripDoc.data();
         
         // Only the trip owner can remove users
         if (tripData.createdBy !== currentUserId) {
            throw new Error('Only the trip owner can remove users');
         }
         
         // Find and delete the participation record
         const participantQuery = query(
            collection(db, 'tripParticipants'),
            where('tripId', '==', tripId),
            where('userId', '==', userId)
         );
         
         const participantSnapshot = await getDocs(participantQuery);
         
         if (!participantSnapshot.empty) {
            await deleteDoc(participantSnapshot.docs[0].ref);
         }
      } catch (error) {
         console.error('Error removing user from trip:', error);
         throw error;
      }
   }

   /**
    * Update a user's role in a trip
    */
   async updateParticipantRole(participantId: string, newRole: TripRole): Promise<void> {
      try {
         await updateDoc(doc(db, 'tripParticipants', participantId), {
            role: newRole,
         });
      } catch (error) {
         console.error('Error updating participant role:', error);
         throw error;
      }
   }

   /**
    * Get all trips a user has been invited to
    */
   async getUserInvitations(userEmail: string): Promise<TripInvitation[]> {
      try {
         const q = query(
            collection(db, 'tripParticipants'),
            where('userEmail', '==', userEmail),
            where('status', '==', 'pending'),
            orderBy('invitedAt', 'desc')
         );

         const querySnapshot = await getDocs(q);
         const invitations: TripInvitation[] = [];

         for (const docSnapshot of querySnapshot.docs) {
            const data = docSnapshot.data();

            // Get inviter details
            let inviterName = 'Unknown User';
            let inviterEmail = '';
            let inviterPhotoURL = '';

            try {
               const userDocRef = doc(db, 'users', data.invitedBy);
               const userDocSnap = await getDoc(userDocRef);

               if (userDocSnap.exists()) {
                  const userData = userDocSnap.data();
                  inviterName =
                     userData.displayName || userData.email?.split('@')[0] || 'Unknown User';
                  inviterEmail = userData.email || '';
                  inviterPhotoURL = userData.photoURL || '';
               }
            } catch (err) {
               console.error('Error fetching inviter details:', err);
            }

            invitations.push({
               id: docSnapshot.id,
               tripId: data.tripId,
               tripTitle: data.tripTitle || 'Unnamed Trip',
               invitedBy: {
                  id: data.invitedBy,
                  name: inviterName,
                  email: inviterEmail,
                  photoURL: inviterPhotoURL,
               },
               role: data.role,
               invitedAt: data.invitedAt.toDate(),
            });
         }

         return invitations;
      } catch (error) {
         console.error('Error getting user invitations:', error);
         throw error;
      }
   }

   /**
    * Get all trips a user has access to
    */
   async getUserTrips(userId: string): Promise<string[]> {
      try {
         const tripIds: string[] = [];
         
         // Get trips where user is a participant with accepted status
         const participantQuery = query(
            collection(db, 'tripParticipants'),
            where('userId', '==', userId),
            where('status', '==', 'accepted')
         );
         
         const participantSnapshot = await getDocs(participantQuery);
         
         participantSnapshot.forEach(doc => {
            const data = doc.data();
            tripIds.push(data.tripId);
         });
         
         return tripIds;
      } catch (error) {
         console.error('Error getting user trips:', error);
         return [];
      }
   }

   /**
    * Create a direct chat between a trip owner and a participant when they accept an invitation
    * This ensures that people who share a trip can always chat with each other
    */
   async createDirectChatOnAccept(tripId: string, userId: string): Promise<boolean> {
      try {
         // Get trip details to find owner
         const tripDoc = await getDoc(doc(db, 'trips', tripId));
         
         if (!tripDoc.exists()) {
            console.error(`Trip ${tripId} not found when creating direct chat`);
            return false;
         }
         
         const tripData = tripDoc.data();
         const ownerId = tripData.createdBy;
         
         // Don't create a chat if owner is the same as the user
         if (ownerId === userId) {
            return true;
         }
         
         // Record the chat connection in Firestore for tracking
         await addDoc(collection(db, 'chatMembers'), {
            tripId,
            userId,
            ownerId,
            createdAt: serverTimestamp(),
            channelType: 'messaging',
            channelId: await chatService.getDirectChatId(userId, ownerId)
         });
         
         return true;
      } catch (error) {
         console.error('Error creating direct chat on accept:', error);
         return false;
      }
   }
}

export const tripSharingService = new TripSharingService();
export default tripSharingService;
